import _ from 'lodash';

Mobi2Go._class = Mobi2Go._class || {};

const { Price } = Mobi2Go._class.Common;
let cid = 0;
const { mixinEvents } = Mobi2Go.app;
const modifier_maximum_quantity = 2500;

/*------------------------------------------------------------------------------
 * Order Modifier Constructor
 */
var Modifier = (Mobi2Go._class.OrderProductModifier = function(
    menu_modifier,
    parent_group
) {
    if (!(this instanceof Modifier)) {
        return new Modifier(menu_modifier, parent_group);
    }

    this.init(menu_modifier, parent_group);
});

_.extend(Modifier.prototype, {
    /*--------------------------------------------------------------------------
     * Initiate
     */
    init(menu_modifier, parent_group) {
        if (!(menu_modifier instanceof Mobi2Go._class.MenuItem.Modifier)) {
            throw new TypeError(
                'menu_product must be an instance of Mobi2Go._class.MenuItem.Modifier'
            );
        }

        if (
            !(parent_group instanceof Mobi2Go._class.OrderProductModifierGroup)
        ) {
            throw new TypeError(
                'menu_product must be an instance of Mobi2Go._class.MenuItem.OrderProductModifierGroup'
            );
        }

        this.cid = cid++;
        mixinEvents(`OrderProductModifier#${this.cid}`, this);

        this.id = null;
        this.menu_modifier = menu_modifier;
        this.parent_group = parent_group;
        this.parent_product = parent_group.parent_product;
        this.is_default = _.includes(
            this.parent_product.menu_product.modifiers,
            menu_modifier.id
        );

        this.price = this.menu_modifier.getPrice();
        this.selection_state = this.is_default ? 'on' : 'off';
        this.quantity = this.is_default ? 1 : 0;
        this.total = new Price(0.0); // Remove?
        this.has_size_prices = null;
        this.has_size_tiers = null;
        this.visible = true;
        this.out_of_stock = false;
        this._updateRestriction = _.bind(this.updateRestriction, this);
        this._updatePrice = _.bind(this.updatePrice, this);

        if (this.hasSizePrices() || this.hasSizeTiers()) {
            this.parent_product.bind('initiated', this._updatePrice);
            this.parent_product.bind('updateSize', this._updatePrice);
        }

        if (this.parent_product.hasModifierCreditLimit()) {
            this.parent_product.bind({
                updateModifierCredit: this._updatePrice,
                updateModifierCreditLimit: this._updatePrice,
            });
        }

        Mobi2Go.Order.bind({
            updateMenuRestrictions: this._updateRestriction,
        });

        this.updateRestriction();
        this.updatePrice();
    },

    getMenuModifierProperty(key) {
        const { size } = this.parent_product;
        if (size) {
            return this.menu_modifier.getPropertyBySize(
                key,
                size.menu_modifier
            );
        }
        return this.menu_modifier.getProperty(key);
    },

    updateRestriction() {
        const is_hidden = Mobi2Go.Order.isModifierHidden(this.menu_modifier);
        const is_out_of_stock = Mobi2Go.Order.isModifierSoldOut(
            this.menu_modifier
        );
        this.setVisible(!is_hidden);
        this.setOutOfStock(is_out_of_stock);
        this.parent_group.updateRestriction();
        // Force re-apply selection state
        this.parent_product.setModifierSelectionState(
            this,
            this.selection_state
        );
    },

    setVisible(visible) {
        visible = !!visible;
        if (this.visible === visible) {
            return;
        }
        this.visible = visible;
        this.parent_product._triggerUpdateModifierVisiblity(this);
    },

    setOutOfStock(is_out_of_stock) {
        is_out_of_stock = !!is_out_of_stock;
        if (is_out_of_stock === this.out_of_stock) {
            return;
        }
        this.out_of_stock = !!is_out_of_stock;
        this.parent_product._triggerUpdateModifierOutOfStock(this);
    },

    /**-------------------------------------------------------------------------
     */
    unbindFromProduct() {
        if (this.hasSizePrices()) {
            this.parent_product.unbind('updateSize', this._updatePrice);
        }

        if (this.parent_product.hasModifierCreditLimit()) {
            this.parent_product.unbind({
                updateModifierCredit: this._updatePrice,
                updateModifierCreditLimit: this._updatePrice,
            });
        }

        Mobi2Go.Order.unbind({
            updateMenuRestrictions: this._updateRestriction,
        });
    },

    /**-------------------------------------------------------------------------
     */
    set(properties) {
        if (properties.id !== undefined) {
            this.id = properties.id;
        }

        if (properties.selection_state !== undefined) {
            this.setSelectionState(properties.selection_state);
        }
        if (properties.quantity !== undefined) {
            this.setQuantity(properties.quantity);
        }
    },

    /*--------------------------------------------------------------------------
     */
    reset() {
        this.setSelectionState(this.is_default ? 'on' : 'off');
        this.setQuantity(this.is_default ? 1 : 0);
        this.updatePrice();
    },

    /*--------------------------------------------------------------------------
     */
    setPrice(amount) {
        const new_price = new Price(amount);
        if (!this.price.isEqual(new_price)) {
            this.price = new_price;
            this.parent_product._triggerUpdateModifierPrice(this);
        }
    },

    /*--------------------------------------------------------------------------
     * The price of a  modifier can change when:
     * - Parent product size changes
     * - Menu 'tier' is changed
     */
    updatePrice() {
        let price;
        const { parent_product } = this;

        if (this.hasSizePrices() && this.getSizePrice()) {
            price = this.getSizePrice();
        } else {
            price = new Price(this.getMenuModifierProperty('price')).toFloat();
        }

        if (
            parent_product.hasModifierCreditLimit() &&
            !this.hasCreditLimitOverride()
        ) {
            const remaining_modifier_credit = parent_product.getRemainingModifierCredit();
            if (price > remaining_modifier_credit) {
                price = remaining_modifier_credit;
            }
        }

        this.setPrice(price);
    },

    /*--------------------------------------------------------------------------
     */
    hasSizePrices() {
        if (this.has_size_prices === null) {
            this.has_size_prices =
                this.parent_product.size_options.length &&
                this.menu_modifier.hasSizePrices();
        }

        return this.has_size_prices;
    },

    /*--------------------------------------------------------------------------
     */
    getSizePrice() {
        const { size } = this.parent_product;

        if (size) {
            return this.menu_modifier.getPriceBySize(size.menu_modifier);
        }

        return null;
    },

    hasSizeTiers() {
        if (this.has_size_tiers === null) {
            this.has_size_tiers =
                this.parent_product.size_options.length &&
                this.menu_modifier.hasSizeTiers();
        }
        return this.has_size_tiers;
    },

    /*--------------------------------------------------------------------------
     * Sets quantity of modifier - checks quantity does not exceed and
     * product or modifier group selection limits
     */
    setQuantity(new_quantity) {
        const group_selection_limit = this.parent_group.selection_limit;
        const that = this;

        new_quantity = +new_quantity;
        if (new_quantity < 0 || _.isNaN(new_quantity)) {
            new_quantity = 0;
        } else if (this.menu_modifier.type !== 'quantity' && new_quantity > 1) {
            new_quantity = 1;
        }

        if (new_quantity > modifier_maximum_quantity) {
            new_quantity = modifier_maximum_quantity;
        }

        if (this.quantity === new_quantity) {
            return;
        }

        // PRODUCT SELECTION LIMIT
        const product_selection_limit = this.parent_product.menu_product
            .modifier_selection_limit;
        const has_limit_override = this.parent_group.menu_modifier_group
            .product_modifier_selection_limit_override;

        if (product_selection_limit > 0 && !has_limit_override) {
            const product_count =
                this.parent_product.getModifierSelectionCount() - this.quantity;

            if (product_count + new_quantity > product_selection_limit) {
                new_quantity = product_selection_limit - product_count;
                this.parent_product._triggerSelectionLimitWarning(this);
            }
        }

        // AUTO-UNSELECT OTHER MODIFEIRS
        if (new_quantity && group_selection_limit === 1) {
            _.each(this.parent_group.modifiers, function(modifier) {
                const { menu_modifier } = modifier;
                if (
                    that.menu_modifier.id !== menu_modifier.id &&
                    menu_modifier.type === 'boolean' &&
                    modifier.quantity === 1
                ) {
                    that.parent_product.toggleModifierById(menu_modifier.id);
                }
            });
        }

        // MODIFIER GROUP SELECTION LIMIT
        if (group_selection_limit > 0) {
            const group_count = this.parent_group.selection_count;
            const new_group_count = group_count - this.quantity + new_quantity;

            if (new_group_count > group_selection_limit) {
                new_quantity =
                    group_selection_limit - (group_count - this.quantity);
                this.parent_product._triggerGroupSelectionLimitWarning(
                    this.parent_group,
                    this
                );
                if (new_quantity < 0) {
                    new_quantity = 0;
                }
                if (new_quantity === this.quantity) {
                    return;
                }
            }
        }

        this.quantity = new_quantity;

        if (this.menu_modifier.type === 'quantity') {
            this.setSelectionState(this.quantity ? 'on' : 'off');
            this.parent_product._triggerUpdateModifierQuantity(this);
            this.trigger('updateQuantity', this);
        }

        this.parent_group.updateSelectionCount();
    },

    canQuantityBeIncreased() {
        if (this.quantity === modifier_maximum_quantity) {
            return false;
        }

        const product_selection_limit = this.parent_product.menu_product
            .modifier_selection_limit;
        const has_limit_override = this.parent_group.menu_modifier_group
            .product_modifier_selection_limit_override;

        if (
            product_selection_limit > 0 &&
            !has_limit_override &&
            this.parent_product.getModifierSelectionCount() >=
                product_selection_limit
        ) {
            return false;
        }

        if (
            this.parent_group.selection_limit > 0 &&
            this.parent_group.selection_count >=
                this.parent_group.selection_limit
        ) {
            return false;
        }

        return true;
    },

    /*--------------------------------------------------------------------------
     */
    setSelectionState(new_selection_state) {
        const { type } = this.menu_modifier;

        if (this.out_of_stock || !this.visible) {
            new_selection_state = 'off';
            this.setQuantity(0);
        } else if (type === 'boolean' || type === 'style') {
            if (new_selection_state === 'off') {
                this.setQuantity(0);
            } else if (new_selection_state === 'on') {
                this.setQuantity(1);
            } else {
                return;
            }
        } else if (type === 'fractional') {
            switch (new_selection_state) {
                case 'off':
                    this.setQuantity(0);
                    break;
                case 'on':
                case 'left':
                case 'right':
                    this.setQuantity(1);
                    break;
                default:
                    return;
            }
        } else if (
            type === 'quantity' &&
            new_selection_state === 'on' &&
            !this.quantity
        ) {
            this.setQuantity(1);
        }

        // If quantity = 0 then a product or modifier group selection limit
        // may have been exceeded so force selection state to be 'off'
        if (!this.quantity) {
            new_selection_state = 'off';
        }

        if (this.selection_state !== new_selection_state) {
            this.selection_state = new_selection_state;
            this.parent_product._triggerUpdateModifierSelection(this);
            this.trigger('updateSelectionState', this);
        }
    },

    /*--------------------------------------------------------------------------
     * Cycle through states i.e "on" -> "off" -> "on" -> "off"
     */
    toggle() {
        const { type } = this.menu_modifier;
        if (type === 'boolean' || type === 'style') {
            switch (this.selection_state) {
                case 'on':
                    this.setSelectionState('off');
                    break;
                case 'off':
                    this.setSelectionState('on');
                    break;
            }
        } else if (type === 'fractional') {
            switch (this.selection_state) {
                case 'off':
                    this.setSelectionState('on');
                    break;
                case 'on':
                    this.setSelectionState('left');
                    break;
                case 'left':
                    this.setSelectionState('right');
                    break;
                case 'right':
                    this.setSelectionState('off');
                    break;
            }
        }
    },

    /*--------------------------------------------------------------------------
     */
    isAdded() {
        return !this.is_default && this.quantity;
    },

    /*--------------------------------------------------------------------------
     */
    isRemoved() {
        return this.is_default && this.selection_state !== 'on';
    },

    /*--------------------------------------------------------------------------
     */
    hasCreditLimitOverride() {
        return this.menu_modifier.credit_limit_override;
    },

    /*--------------------------------------------------------------------------
     */
    toJson() {
        return {
            id: this.id,
            menu_modifier: _.pick(this.menu_modifier, ['id', 'name']),
            selection_state: this.selection_state,
            quantity: this.quantity,
        };
    },
});

/*------------------------------------------------------------------------------
 * toString - extend doesn't work on prototype in IE8...
 */
Modifier.prototype.toString = function() {
    let name = this.menu_modifier.toString();
    if (this.isRemoved() || this.isAdded()) {
        if (this.selection_state === 'left') {
            name += ' (left side)';
        } else if (this.selection_state === 'right') {
            name += ' (right side)';
        }
        if (this.quantity > 1) {
            name = `${this.quantity}× ${name}`;
        }
    }
    return name;
};
